// Shapes and labels for the Contract compliance page (db/migrations/3800 to
// 3806, docs/CONTRACT-COMPLIANCE.md). Postgres decides every figure; this file
// only names them.

export type ObligationStatus = 'in_place' | 'partial' | 'missing'

export type Obligation = {
  id: number
  area: string
  clause: string
  obligation: string
  found: string
  status: ObligationStatus
  status_as_found: string
  gap: string
  build_item: string
  wave: number
  status_changed_at: string | null
  status_evidence: string | null
  open_deadlines: number
  overdue_deadlines: number
  records: number
}

export type Deadline = {
  id: string
  rule: string
  title: string
  clause: string
  driver_id: string | null
  driver: string | null
  due_on: string
  anchor_date: string
  overdue_days: number
  escalation_level: number
  task_id: string | null
  task_ref: number | null
  owners: string[]
  detail: string | null
}

export type ClosedDeadline = {
  rule: string
  title: string
  clause: string
  driver: string | null
  due_on: string
  done_at: string
  done_by: string
  status: 'done' | 'cancelled'
  late: boolean
}

export type DriverRow = {
  driver_id: string
  name: string
  status: string
  terms: string
  agreement_version: string | null
  agreement_signed_on: string | null
  addendum_terms_from: string | null
  records: number
  awaiting: number
  overdue: number
  open_deadlines: number
}

export type Setting = { key: string; clause: string; value: string; unit: string; from: string; note: string | null }

export type Drift = {
  setting_key: string
  clause: string
  contract_value: number
  source: string
  source_key: string
  source_value: string | null
  finding: string
}

export type CompliancePage = {
  as_of: string
  is_admin: boolean
  summary: {
    obligations: number
    in_place: number
    partial: number
    missing: number
    open_deadlines: number
    overdue_deadlines: number
    records_awaiting: number
    records_overdue: number
    setting_drift: number
  }
  obligations: Obligation[]
  deadlines: Deadline[]
  recently_closed: ClosedDeadline[]
  drivers: DriverRow[]
  settings: Setting[]
  drift: Drift[]
}

export type EmployeeRecord = {
  id: string
  type: string
  label: string
  clause: string
  title: string | null
  given_on: string
  given_by: string | null
  sign_due_on: string | null
  status: string
  acknowledgement: 'signature' | 'none'
  signed_at: string | null
  sign_channel: string | null
  response: string | null
  witness: string | null
  witness_person_id: string | null
  witness_confirmed_at: string | null
  paper_path: string | null
  paper_signed_on: string | null
  envelope_id: string | null
  document_text: string
  void_reason: string | null
  overdue: boolean
  message_status: string | null
}

export type DriverRecords = {
  me: string
  driver: string | null
  staff: { person_id: string; name: string }[]
  records: EmployeeRecord[]
}

export const OBLIGATION_STATUS: Record<ObligationStatus, { label: string; tone: string }> = {
  in_place: { label: 'In place', tone: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200' },
  partial: { label: 'Partial', tone: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200' },
  missing: { label: 'Missing', tone: 'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-200' },
}

export const RECORD_STATUS: Record<string, string> = {
  issued: 'Given, for his records',
  awaiting_signature: 'Waiting for him to acknowledge',
  signed: 'Acknowledged on his page',
  refused_awaiting_witness: 'Refused, waiting for the witness',
  refused_witnessed: 'Refused, witnessed',
  paper_signed: 'Signed on paper',
  void: 'Void',
}

export const MESSAGE_STATUS: Record<string, string> = {
  held: 'Email held',
  pending: 'Email queued',
  sent: 'Email sent',
  failed: 'Email failed',
}

export function day(s: string | null | undefined): string {
  if (!s) return ''
  const d = new Date(String(s).slice(0, 10) + 'T12:00:00')
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function todaySast(): string {
  return new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10)
}
